import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Select, MenuItem, Checkbox, FormControlLabel, Tooltip as MuiTooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import Button from '../../lib/button/Button';
import Tooltip from '../../lib/tooltip/Tooltip';
import SmallDialog from '../../lib/dialog/SmallDialog';
import DropdownMenuPopup from '../../lib/dropdown/DropdownMenuPopup';
import { getAccessToken } from '../../auth';
import { envConfig } from '../../config';
import { useWorkspaceStore } from '../../store/workspace';
import { popupNotification } from '../../popup-notification';
import { ProPlanChip } from '../ProPlanChip';
import { HierarchicalScopeBuilder } from './HierarchicalScopeBuilder';
import '../project-configuration/project-configuration-content.css';

export interface IApiTokensSectionProps {
	workspaceId?: string;
}

export interface IApiTokenData {
	id: string;
	name: string;
	displayToken: string;
	type: 'personal' | 'service';
	roleId?: string | null;
	createdBySystem?: boolean;
	userId: string | null;
	workspaceId: string;
	environment: string;
	scopes: string[];
	expiresAt: string | null;
	revokedAt: string | null;
	lastUsedAt: string | null;
	createdAt: string;
}

type ScopePreset = 'full' | 'read' | 'custom';

const ScopeTagPill: React.FC<{
	scope: string;
	isInactive: boolean;
}> = ({ scope, isInactive }) => {
	const [hovered, setHovered] = useState(false);
	const [copied, setCopied] = useState(false);

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(scope);
		setCopied(true);
		popupNotification({
			text: `Scope "${scope}" copied to clipboard`,
			color: 'success'
		});
		setTimeout(() => setCopied(false), 1500);
	};

	return (
		<code
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			style={{
				fontSize: '11px',
				backgroundColor: isInactive ? '#f1f5f9' : '#e0e7ff',
				color: isInactive ? '#64748b' : '#4338ca',
				padding: '2px 8px',
				borderRadius: '4px',
				fontFamily: 'monospace',
				display: 'inline-flex',
				alignItems: 'center',
				gap: '5px',
				cursor: 'pointer',
				position: 'relative',
				transition: 'all 0.15s ease'
			}}
		>
			<span>{scope}</span>
			{hovered && (
				<i
					className={copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'}
					onClick={handleCopy}
					title="Copy scope"
					style={{
						fontSize: '10px',
						color: isInactive ? '#475569' : '#3730a3',
						cursor: 'pointer'
					}}
				/>
			)}
		</code>
	);
};

export const ApiTokensSection: React.FC<IApiTokensSectionProps> = ({
	workspaceId: customWorkspaceId
}) => {
	const navigate = useNavigate();
	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);
	const targetWorkspaceId = customWorkspaceId || activeWorkspace?.id;

	const hasApiTokensEntitlement =
		activeWorkspace?.features?.apiTokens === true ||
		(activeWorkspace as any)?.planTier === 'pro' ||
		(activeWorkspace as any)?.planTier === 'organization';

	const [tokens, setTokens] = useState<IApiTokenData[] | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
	const [tokenName, setTokenName] = useState<string>('');
	const [tokenType, setTokenType] = useState<'personal' | 'service'>('personal');
	const [expirationDate, setExpirationDate] = useState<Dayjs | null>(dayjs().add(30, 'day'));
	const [neverExpires, setNeverExpires] = useState<boolean>(false);

	// Scope States
	const [scopePreset, setScopePreset] = useState<ScopePreset>('full');
	const [customScopes, setCustomScopes] = useState<string[]>(['*']);

	const [isCreating, setIsCreating] = useState<boolean>(false);
	const [createdRawToken, setCreatedRawToken] = useState<string | null>(null);
	const [copied, setCopied] = useState<boolean>(false);

	// Expansion state for raw scopes
	const [expandedTokenId, setExpandedTokenId] = useState<string | null>(null);
	const [copiedAllTokenId, setCopiedAllTokenId] = useState<string | null>(null);

	// Revoke Dialog State
	const [revokeTargetToken, setRevokeTargetToken] = useState<IApiTokenData | null>(null);
	const [revokeImmediately, setRevokeImmediately] = useState<boolean>(true);
	const [scheduledRevocationDate, setScheduledRevocationDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
	const [isRevoking, setIsRevoking] = useState<boolean>(false);

	const fetchTokens = useCallback(async () => {
		if (!targetWorkspaceId) return;
		setIsLoading(true);
		try {
			const token = await getAccessToken();
			const res = await axios.get(
				`${envConfig.API_BASE_URL}/user-service/v1/api-tokens?workspaceId=${targetWorkspaceId}`,
				{
					headers: {
						Authorization: `Bearer ${token}`
					}
				}
			);
			setTokens(res.data?.data || []);
		} catch (err: any) {
			popupNotification({
				text: `Failed to load API tokens: ${err?.response?.data?.message || err.message}`,
				color: 'error'
			});
		} finally {
			setIsLoading(false);
		}
	}, [targetWorkspaceId]);

	useEffect(() => {
		fetchTokens();
	}, [fetchTokens]);

	// Token Status Helper
	const getTokenStatus = (t: IApiTokenData) => {
		const now = Date.now();
		const isRevoked = !!(t.revokedAt && new Date(t.revokedAt).getTime() <= now);
		const isScheduledRevocation = !!(t.revokedAt && new Date(t.revokedAt).getTime() > now);
		const isExpired = !!(t.expiresAt && new Date(t.expiresAt).getTime() <= now);
		const isInactive = isRevoked || isExpired;
		return { isRevoked, isScheduledRevocation, isExpired, isInactive };
	};

	// Sorted Tokens (Active/Scheduled first, Expired/Revoked at bottom)
	const sortedTokens = useMemo(() => {
		if (!tokens) return [];
		return [...tokens].sort((a, b) => {
			const statusA = getTokenStatus(a);
			const statusB = getTokenStatus(b);

			if (statusA.isInactive !== statusB.isInactive) {
				return statusA.isInactive ? 1 : -1;
			}
			return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
		});
	}, [tokens]);

	// Compute active scopes list
	const computedScopes = useMemo<string[]>(() => {
		if (scopePreset === 'full') return ['*'];
		if (scopePreset === 'read') return ['*:read'];
		return customScopes && customScopes.length > 0 ? customScopes : ['*'];
	}, [scopePreset, customScopes]);

	const handleGenerateClick = () => {
		if (!hasApiTokensEntitlement) {
			navigate('/pricing/plans');
			return;
		}
		setCreateDialogOpen(true);
	};

	const handleCreateToken = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!tokenName.trim() || !targetWorkspaceId) return;

		if (!neverExpires) {
			if (!expirationDate) {
				popupNotification({
					text: 'Please select a valid expiration date and time.',
					color: 'error'
				});
				return;
			}
			if (expirationDate.valueOf() <= Date.now()) {
				popupNotification({
					text: 'Expiration date and time must be in the future.',
					color: 'error'
				});
				return;
			}
		}

		setIsCreating(true);
		try {
			const token = await getAccessToken();
			const finalExpiresAt = neverExpires ? null : expirationDate ? expirationDate.toISOString() : null;

			const res = await axios.post(
				`${envConfig.API_BASE_URL}/user-service/v1/api-tokens`,
				{
					name: tokenName.trim(),
					workspaceId: targetWorkspaceId,
					type: tokenType,
					scopes: computedScopes,
					expiresAt: finalExpiresAt
				},
				{
					headers: {
						Authorization: `Bearer ${token}`
					}
				}
			);

			const newTokenData = res.data?.data;
			setCreatedRawToken(newTokenData?.token);
			setTokens((prev) => [newTokenData, ...(prev || [])]);
			popupNotification({
				text: 'API token created successfully',
				color: 'success'
			});
		} catch (err: any) {
			popupNotification({
				text: `Failed to create token: ${err?.response?.data?.message || err.message}`,
				color: 'error'
			});
		} finally {
			setIsCreating(false);
		}
	};

	const handleOpenRevokeModal = (tokenToRevoke: IApiTokenData) => {
		setRevokeTargetToken(tokenToRevoke);
		setRevokeImmediately(true);
		setScheduledRevocationDate(dayjs().add(1, 'day'));
	};

	const handleConfirmRevoke = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!revokeTargetToken || !targetWorkspaceId) return;

		let finalRevokedAt: string | null = null;
		if (!revokeImmediately) {
			if (!scheduledRevocationDate) {
				popupNotification({
					text: 'Please select a valid revocation date and time.',
					color: 'error'
				});
				return;
			}

			const targetMs = scheduledRevocationDate.valueOf();
			const nowMs = Date.now();

			if (targetMs <= nowMs) {
				popupNotification({
					text: 'Scheduled revocation date and time must be in the future.',
					color: 'error'
				});
				return;
			}

			if (revokeTargetToken.expiresAt) {
				const expMs = new Date(revokeTargetToken.expiresAt).valueOf();
				if (targetMs > expMs) {
					popupNotification({
						text: 'Scheduled revocation date cannot be later than token expiration date.',
						color: 'error'
					});
					return;
				}
			}

			finalRevokedAt = scheduledRevocationDate.toISOString();
		}

		setIsRevoking(true);
		try {
			const token = await getAccessToken();
			await axios.delete(
				`${envConfig.API_BASE_URL}/user-service/v1/api-tokens/${revokeTargetToken.id}?workspaceId=${targetWorkspaceId}`,
				{
					headers: {
						Authorization: `Bearer ${token}`
					},
					data: {
						revokedAt: finalRevokedAt
					}
				}
			);

			popupNotification({
				text: finalRevokedAt ? 'API token revocation scheduled successfully' : 'API token revoked immediately',
				color: 'success'
			});
			fetchTokens();
			setRevokeTargetToken(null);
		} catch (err: any) {
			popupNotification({
				text: `Failed to revoke token: ${err?.response?.data?.message || err.message}`,
				color: 'error'
			});
		} finally {
			setIsRevoking(false);
		}
	};

	const handleCancelRevocation = async (tokenToCancel: IApiTokenData) => {
		if (!targetWorkspaceId) return;
		try {
			const token = await getAccessToken();
			await axios.post(
				`${envConfig.API_BASE_URL}/user-service/v1/api-tokens/${tokenToCancel.id}/cancel-scheduled-revocation?workspaceId=${targetWorkspaceId}`,
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`
					}
				}
			);

			popupNotification({
				text: 'API token revocation cancelled successfully',
				color: 'success'
			});
			fetchTokens();
		} catch (err: any) {
			popupNotification({
				text: `Failed to cancel revocation: ${err?.response?.data?.message || err.message}`,
				color: 'error'
			});
		}
	};

	const handleCopyToken = () => {
		if (createdRawToken) {
			navigator.clipboard.writeText(createdRawToken);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const handleCopyAllScopes = (e: React.MouseEvent, tokenId: string, scopes: string[]) => {
		e.stopPropagation();
		const formatted = (scopes && scopes.length > 0 ? scopes : ['*']).join(', ');
		navigator.clipboard.writeText(formatted);
		setCopiedAllTokenId(tokenId);
		popupNotification({
			text: 'All scopes copied to clipboard',
			color: 'success'
		});
		setTimeout(() => setCopiedAllTokenId(null), 1500);
	};

	const resetCreateModal = () => {
		setCreateDialogOpen(false);
		setTokenName('');
		setTokenType('personal');
		setExpirationDate(dayjs().add(30, 'day'));
		setNeverExpires(false);
		setScopePreset('full');
		setCustomScopes(['*']);
		setCreatedRawToken(null);
		setCopied(false);
	};

	const toggleExpandRow = (tokenId: string) => {
		setExpandedTokenId((prev) => (prev === tokenId ? null : tokenId));
	};

	const isCustomScope = scopePreset === 'custom';

	return (
		<section style={{ marginTop: '16px' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '8px'
				}}
			>
				<div>
					<h3
						style={{
							display: 'flex',
							gap: '6px',
							fontWeight: 'normal',
							margin: '0px'
						}}
					>
						API Tokens
					</h3>
					<p
						style={{
							margin: '0px',
							fontSize: '13px',
							color: '#6b7280',
							marginTop: '4px'
						}}
					>
						Manage secret tokens for serverless execution, automated tasks, and API integrations.
					</p>
				</div>

				<div
					id="generate-token-button-wrapper"
					style={{ display: 'flex', alignItems: 'center', gap: '0px' }}
				>
					<Button
						variant="text"
						iconStart="fa-solid fa-plus"
						color="grey"
						size="small"
						id="add-api-token-button"
						style={!hasApiTokensEntitlement ? { color: '#9ca3af', opacity: 0.7 } : undefined}
						onClick={handleGenerateClick}
					>
						Generate token
					</Button>
					{!hasApiTokensEntitlement && (
						<span
							onClick={handleGenerateClick}
							style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
						>
							<ProPlanChip />
						</span>
					)}
					<Tooltip
						anchorQuerySelector="#add-api-token-button"
						placement="bottom"
					>
						{hasApiTokensEntitlement
							? 'Generate a new secret API token for programmatic or server execution'
							: 'Click to upgrade to a Pro plan and generate API tokens'}
					</Tooltip>
				</div>
			</div>

			<div className="project-roles-wrapper" style={{ minHeight: '160px' }}>
				{isLoading ? (
					<div
						style={{
							padding: '32px 16px',
							textAlign: 'center',
							color: '#6b7280',
							fontSize: '14px'
						}}
					>
						Loading API tokens...
					</div>
				) : sortedTokens && sortedTokens.length > 0 ? (
					<div className="project-roles-list" style={{ maxHeight: '480px', overflowY: 'auto' }}>
						{sortedTokens.map((t) => {
							const isSystemCreated = t.createdBySystem === true || !t.userId;
							const tStatus = getTokenStatus(t);
							const isExpanded = expandedTokenId === t.id;

							const scopeSummary =
								!t.scopes || t.scopes.includes('*')
									? 'Full Access (*)'
									: t.scopes.includes('*:read')
									? 'Read Only (*:read)'
									: `${t.scopes.length} Custom Scopes`;

							return (
								<div
									key={t.id}
									className="project-role-item"
									onClick={() => toggleExpandRow(t.id)}
									style={{
										borderBottom: '1px solid #f3f4f6',
										cursor: 'pointer',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'stretch',
										padding: '12px 16px',
										transition: 'background-color 0.15s ease',
										backgroundColor: isExpanded ? '#f8fafc' : 'transparent',
										opacity: tStatus.isInactive ? 0.85 : 1
									}}
									onMouseEnter={(e) => {
										if (!isExpanded) e.currentTarget.style.backgroundColor = '#f8fafc';
									}}
									onMouseLeave={(e) => {
										if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
									}}
								>
									{/* Main Row Container */}
									<div
										style={{
											display: 'flex',
											alignItems: 'flex-start',
											justifyContent: 'space-between',
											width: '100%',
											gap: '12px'
										}}
									>
										{/* Left Side: Icon Column & Title Content */}
										<div
											style={{
												display: 'flex',
												alignItems: 'flex-start',
												gap: '12px',
												flex: 1,
												minWidth: 0
											}}
										>
											{/* Icon Column (Key Icon + Copy All Button when expanded) */}
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'center',
													gap: '8px',
													flexShrink: 0
												}}
											>
												<div
													style={{
														width: '32px',
														height: '32px',
														borderRadius: '50%',
														backgroundColor: tStatus.isInactive
															? '#f1f5f9'
															: isSystemCreated
															? '#f3f4f6'
															: '#e0e7ff',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														color: tStatus.isInactive
															? '#94a3b8'
															: isSystemCreated
															? '#6b7280'
															: '#4f46e5',
														fontSize: '14px'
													}}
												>
													<i className={isSystemCreated ? 'fa-solid fa-robot' : 'fa-solid fa-key'} />
												</div>

												{/* Circular Icon-Only Copy All Button (Rendered below icon when expanded) */}
												{isExpanded && (
													<MuiTooltip title="Copy all scopes separated by commas" placement="bottom">
														<button
															onClick={(e: React.MouseEvent) => handleCopyAllScopes(e, t.id, t.scopes)}
															style={{
																width: '24px',
																height: '24px',
																borderRadius: '50%',
																border: '1px solid #cbd5e1',
																backgroundColor: 'white',
																color: copiedAllTokenId === t.id ? '#16a34a' : '#64748b',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																fontSize: '11px',
																cursor: 'pointer',
																transition: 'all 0.15s ease',
																outline: 'none',
																marginTop: '2px'
															}}
															onMouseEnter={(e) => {
																e.currentTarget.style.backgroundColor = '#f1f5f9';
																e.currentTarget.style.borderColor = '#94a3b8';
																if (copiedAllTokenId !== t.id) e.currentTarget.style.color = '#334155';
															}}
															onMouseLeave={(e) => {
																e.currentTarget.style.backgroundColor = 'white';
																e.currentTarget.style.borderColor = '#cbd5e1';
																if (copiedAllTokenId !== t.id) e.currentTarget.style.color = '#64748b';
															}}
														>
															<i className={copiedAllTokenId === t.id ? 'fa-solid fa-check' : 'fa-regular fa-copy'} />
														</button>
													</MuiTooltip>
												)}
											</div>

											{/* Content Column */}
											<div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
												<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
													<span
														style={{
															fontWeight: 500,
															fontSize: '13.5px',
															color: tStatus.isInactive ? '#64748b' : '#111827'
														}}
													>
														{t.name}
													</span>
													<code
														style={{
															fontSize: '11.5px',
															color: tStatus.isInactive ? '#94a3b8' : '#4b5563',
															backgroundColor: '#f3f4f6',
															padding: '2px 6px',
															borderRadius: '4px',
															fontFamily: 'monospace'
														}}
													>
														{t.displayToken}
													</code>

													{isSystemCreated && (
														<span
															id={`in-use-tag-${t.id}`}
															style={{
																fontSize: '10.5px',
																color: '#2563eb',
																backgroundColor: '#eff6ff',
																border: '1px solid #bfdbfe',
																padding: '1px 6px',
																borderRadius: '4px',
																display: 'flex',
																alignItems: 'center',
																gap: '4px'
															}}
														>
															<i className="fa-solid fa-circle-nodes" style={{ fontSize: '10px' }} />
															In Use by Elyx
														</span>
													)}
												</div>

												<span style={{ fontSize: '11px', color: '#9ca3af' }}>
													Created {new Date(t.createdAt).toLocaleDateString()}
													{tStatus.isRevoked && t.revokedAt ? (
														<>
															{' • '}
															<span style={{ color: '#dc2626', fontWeight: 500 }}>
																Revoked {new Date(t.revokedAt).toLocaleDateString()}
															</span>
														</>
													) : !tStatus.isRevoked && tStatus.isExpired && t.expiresAt ? (
														<>
															{' • '}
															<span style={{ color: '#dc2626', fontWeight: 500 }}>
																Expired at {new Date(t.expiresAt).toLocaleDateString()}
															</span>
														</>
													) : t.expiresAt ? (
														` • Expires ${new Date(t.expiresAt).toLocaleDateString()}`
													) : (
														<>
															{' • '}
															<span style={{ color: '#d97706', fontWeight: 500 }}>
																Never expires
															</span>
														</>
													)}
													{` • ${scopeSummary}`}
												</span>

												{/* Expanded Scope Tag Pills */}
												{isExpanded && (
													<div
														onClick={(e) => e.stopPropagation()}
														style={{
															display: 'flex',
															flexWrap: 'wrap',
															alignItems: 'center',
															justifyContent: 'flex-start',
															gap: '6px',
															marginTop: '6px'
														}}
													>
														{(t.scopes || ['*']).map((sc, idx) => (
															<ScopeTagPill key={idx} scope={sc} isInactive={tStatus.isInactive} />
														))}
													</div>
												)}
											</div>
										</div>

										{/* Right Actions & Status Badges */}
										<div
											onClick={(e) => e.stopPropagation()}
											style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}
										>
											{/* Status Badges */}
											{tStatus.isRevoked && (
												<span
													style={{
														fontSize: '10px',
														fontWeight: 600,
														padding: '2px 7px',
														borderRadius: '4px',
														backgroundColor: '#fef2f2',
														color: '#dc2626',
														border: '1px solid #fecaca'
													}}
												>
													REVOKED
												</span>
											)}

											{tStatus.isScheduledRevocation && (
												<span
													style={{
														fontSize: '10px',
														fontWeight: 600,
														padding: '2px 7px',
														borderRadius: '4px',
														backgroundColor: '#fff7ed',
														color: '#c2410c',
														border: '1px solid #ffedd5'
													}}
												>
													REVOCATION SCHEDULED ({new Date(t.revokedAt!).toLocaleDateString()})
												</span>
											)}

											{!tStatus.isRevoked && tStatus.isExpired && (
												<span
													style={{
														fontSize: '10px',
														fontWeight: 600,
														padding: '2px 7px',
														borderRadius: '4px',
														backgroundColor: '#fef2f2',
														color: '#dc2626',
														border: '1px solid #fecaca'
													}}
												>
													EXPIRED
												</span>
											)}

											{/* Type Badge */}
											<span
												style={{
													fontSize: '10px',
													padding: '3px 8px',
													borderRadius: '4px',
													fontWeight: 600,
													backgroundColor: tStatus.isInactive ? '#e2e8f0' : undefined,
													color: tStatus.isInactive ? '#64748b' : undefined
												}}
												className={
													!tStatus.isInactive
														? `project-role-badge ${
																isSystemCreated
																	? 'role-viewer'
																	: t.type === 'service'
																	? 'role-editor'
																	: 'role-owner'
														  }`
														: undefined
												}
											>
												{isSystemCreated ? 'SYSTEM' : t.type === 'service' ? 'SERVICE' : 'PERSONAL'}
											</span>

											{/* Expand Chevron Icon */}
											<i
												className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
												style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '4px' }}
											/>

											{/* Actions 3-dot Menu (Only for Active tokens) */}
											{!tStatus.isInactive && (
												<>
													<Button
														variant="text"
														iconStart="fa-solid fa-ellipsis-vertical"
														size="small"
														color="grey"
														id={`token-menu-${t.id}`}
													/>
													<DropdownMenuPopup
														popupOptions={{
															anchorQuerySelector: `#token-menu-${t.id}`,
															placement: 'right'
														}}
														options={[
															tStatus.isScheduledRevocation
																? {
																		onClick: () => handleCancelRevocation(t),
																		label: 'Cancel revocation',
																		color: 'primary',
																		iconEnd: 'fa-solid fa-rotate-left'
																  }
																: {
																		onClick: () => handleOpenRevokeModal(t),
																		label: 'Revoke token',
																		color: 'error',
																		iconEnd: 'fa-solid fa-clock-rotate-left'
																  }
														]}
													/>
												</>
											)}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '36px 16px',
							textAlign: 'center'
						}}
					>
						<i
							className="fa-solid fa-key"
							style={{
								fontSize: '32px',
								color: '#d1d5db',
								marginBottom: '10px'
							}}
						/>
						<span
							style={{
								fontSize: '14px',
								fontWeight: 500,
								color: '#374151',
								marginBottom: '4px'
							}}
						>
							No API tokens generated yet
						</span>
						<span
							style={{
								fontSize: '12px',
								color: '#9ca3af',
								maxWidth: '360px'
							}}
						>
							API tokens allow serverless Lambdas and automated external scripts to securely interact with your workspace resources.
						</span>
					</div>
				)}
			</div>

			{/* Create Token Modal */}
			{createDialogOpen && (
				<SmallDialog
					onClose={resetCreateModal}
					style={{
						display: 'flex',
						flexDirection: 'column',
						boxSizing: 'border-box',
						padding: '24px',
						maxHeight: '88vh',
						maxWidth: isCustomScope ? '860px' : '520px',
						width: isCustomScope ? '92vw' : '100%',
						transition: 'all 0.2s ease-in-out'
					}}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							maxHeight: 'calc(88vh - 48px)',
							overflow: 'hidden'
						}}
					>
						{/* Header */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: '16px',
								flexShrink: 0
							}}
						>
							<h2
								style={{
									fontSize: '20px',
									fontWeight: 600,
									color: '#0f172a',
									margin: 0,
									display: 'flex',
									alignItems: 'center',
									gap: '12px'
								}}
							>
								<div
									style={{
										background: '#e0e7ff',
										color: '#4f46e5',
										width: '32px',
										height: '32px',
										borderRadius: '8px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: '16px',
										flexShrink: 0
									}}
								>
									<i className="fa-solid fa-key" />
								</div>
								{createdRawToken ? 'Save Your API Token' : 'Generate API Token'}
							</h2>
							<button
								className="dialog_back_button"
								onClick={resetCreateModal}
								style={{ background: 'none', border: 'none', cursor: 'pointer' }}
							>
								<i className="fa-solid fa-xmark" />
							</button>
						</div>

						{/* Scrollable Form Body */}
						{!createdRawToken ? (
							<form
								id="create-api-token-form"
								onSubmit={handleCreateToken}
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '14px',
									flex: 1,
									overflowY: 'auto',
									paddingRight: '6px'
								}}
							>
								{/* Token Type Selector */}
								<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
										<label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
											Token Type
										</label>
										<i
											id="token-type-help-icon"
											className="fa-regular fa-circle-question"
											style={{ color: '#9ca3af', fontSize: '14px', cursor: 'pointer' }}
										/>
										<Tooltip anchorQuerySelector="#token-type-help-icon" placement="top">
											Personal tokens act on your behalf. Service tokens belong to the workspace for automated bots and CI/CD pipelines.
										</Tooltip>
									</div>

									<Select
										value={tokenType}
										onChange={(e) => setTokenType(e.target.value as 'personal' | 'service')}
										size="small"
										fullWidth
										style={{
											backgroundColor: 'white',
											borderRadius: '8px',
											fontSize: '14px',
											fontWeight: 500
										}}
									>
										<MenuItem
											value="personal"
											style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}
										>
											<i className="fa-solid fa-user" style={{ color: '#4f46e5', width: '18px' }} />
											<span>Personal Access Token</span>
										</MenuItem>
										<MenuItem
											value="service"
											style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}
										>
											<i className="fa-solid fa-robot" style={{ color: '#0284c7', width: '18px' }} />
											<span>Service Token (Workspace / Machine-to-Machine)</span>
										</MenuItem>
									</Select>

									<div
										style={{
											fontSize: '12.5px',
											color: '#4b5563',
											backgroundColor: '#f8fafc',
											border: '1px solid #e2e8f0',
											borderRadius: '6px',
											padding: '8px 12px',
											marginTop: '2px',
											display: 'flex',
											alignItems: 'flex-start',
											gap: '8px'
										}}
									>
										<i
											className={tokenType === 'personal' ? 'fa-solid fa-user' : 'fa-solid fa-robot'}
											style={{
												color: tokenType === 'personal' ? '#4f46e5' : '#0284c7',
												fontSize: '14px',
												marginTop: '2px'
											}}
										/>
										<span>
											{tokenType === 'personal'
												? 'Tied to your user account. Best for local development, CLI commands, and personal scripts.'
												: 'Tied to the workspace. Best for CI/CD pipelines, production Lambdas, and automated M2M integrations.'}
										</span>
									</div>
								</div>

								{/* Token Name Input */}
								<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
									<label
										style={{
											fontSize: '14px',
											fontWeight: 500,
											color: '#374151'
										}}
									>
										Token Name
									</label>
									<input
										type="text"
										placeholder={
											tokenType === 'personal'
												? 'e.g. Local CLI Developer Token'
												: 'e.g. GitHub Actions CI/CD Token'
										}
										value={tokenName}
										onChange={(e) => setTokenName(e.target.value)}
										required
										autoFocus
										style={{
											padding: '10px 14px',
											border: '2px solid #e2e8f0',
											borderRadius: '8px',
											fontSize: '15px',
											outline: 'none',
											transition: 'border-color 0.2s ease'
										}}
										onFocus={(e) => (e.target.style.borderColor = '#4f46e5')}
										onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
									/>
								</div>

								{/* Scope Builder Section */}
								<div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '2px' }}>
									<label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
										Permission Scopes
									</label>

									<Select
										value={scopePreset}
										onChange={(e) => setScopePreset(e.target.value as ScopePreset)}
										size="small"
										fullWidth
										style={{
											backgroundColor: 'white',
											borderRadius: '8px',
											fontSize: '14px'
										}}
									>
										<MenuItem value="full" style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
											<i className="fa-solid fa-globe" style={{ color: '#2563eb', width: '18px' }} />
											<span>Full Access (*)</span>
										</MenuItem>
										<MenuItem value="read" style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
											<i className="fa-solid fa-eye" style={{ color: '#059669', width: '18px' }} />
											<span>Read Only (*:read)</span>
										</MenuItem>
										<MenuItem value="custom" style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
											<i className="fa-solid fa-sliders" style={{ color: '#4f46e5', width: '18px' }} />
											<span>Custom / Hierarchical Scopes</span>
										</MenuItem>
									</Select>

									{scopePreset === 'custom' && targetWorkspaceId && (
										<HierarchicalScopeBuilder
											workspaceId={targetWorkspaceId}
											onChange={(newScopes) => setCustomScopes(newScopes)}
										/>
									)}

									{/* Active Resolved Scope Chips */}
									<div
										style={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: '6px',
											marginTop: '6px'
										}}
									>
										{computedScopes.map((sc, idx) => (
											<code
												key={idx}
												style={{
													fontSize: '11px',
													backgroundColor: '#e0e7ff',
													color: '#4338ca',
													padding: '2px 8px',
													borderRadius: '4px',
													fontFamily: 'monospace'
												}}
											>
												{sc}
											</code>
										))}
									</div>
								</div>

								{/* Expiration Section */}
								<div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
									<label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
										Expiration
									</label>

									<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
										<div style={{ flex: 1 }}>
											<DateTimePicker
												value={expirationDate}
												onChange={(newValue) => setExpirationDate(newValue)}
												disabled={neverExpires}
												minDateTime={dayjs()}
												slotProps={{
													textField: {
														size: 'small',
														fullWidth: true,
														style: { backgroundColor: 'white', borderRadius: '8px' }
													}
												}}
											/>
										</div>

										<FormControlLabel
											control={
												<Checkbox
													checked={neverExpires}
													onChange={(e) => setNeverExpires(e.target.checked)}
													color="primary"
												/>
											}
											label={
												<span style={{ fontSize: '13.5px', color: '#374151', whiteSpace: 'nowrap' }}>
													Never expire
												</span>
											}
										/>
									</div>

									{neverExpires && (
										<div
											style={{
												background: '#fffbe6',
												border: '1px solid #ffe58f',
												padding: '8px 12px',
												borderRadius: '6px',
												fontSize: '12px',
												color: '#8c6b00',
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
												marginTop: '4px'
											}}
										>
											<i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '14px' }} />
											<span>Tokens that never expire pose a security risk if leaked. We recommend setting an expiration date.</span>
										</div>
									)}
								</div>
							</form>
						) : (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '14px',
									flex: 1
								}}
							>
								<div
									style={{
										background: '#fffbe6',
										border: '1px solid #ffe58f',
										padding: '10px 14px',
										borderRadius: '6px',
										fontSize: '13px',
										color: '#8c6b00'
									}}
								>
									<i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '6px' }} />
									Please copy your new API token now. You will not be able to see it again!
								</div>

								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px'
									}}
								>
									<input
										type="text"
										readOnly
										value={createdRawToken}
										style={{
											flex: 1,
											padding: '8px 12px',
											borderRadius: '6px',
											border: '1px solid #ccc',
											fontFamily: 'monospace',
											fontSize: '12px',
											background: '#f9f9f9'
										}}
									/>
									<Button
										variant="outlined"
										color="primary"
										iconStart={copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'}
										onClick={handleCopyToken}
									>
										{copied ? 'Copied' : 'Copy'}
									</Button>
								</div>
							</div>
						)}

						{/* Fixed Footer Buttons Container */}
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								paddingTop: '12px',
								marginTop: '12px',
								borderTop: '1px solid #f1f5f9',
								flexShrink: 0
							}}
						>
							{!createdRawToken ? (
								<>
									<Button
										variant="outlined"
										color="primary"
										onClick={resetCreateModal}
										type="button"
									>
										Cancel
									</Button>
									<Button
										color="primary"
										loading={isCreating}
										type="submit"
										form="create-api-token-form"
										disabled={!tokenName.trim()}
									>
										Generate
									</Button>
								</>
							) : (
								<Button color="primary" onClick={resetCreateModal}>
									Done
								</Button>
							)}
						</div>
					</div>
				</SmallDialog>
			)}

			{/* Scheduled Revocation Modal */}
			{revokeTargetToken && (
				<SmallDialog
					onClose={() => setRevokeTargetToken(null)}
					style={{
						padding: '24px',
						maxWidth: '460px',
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						gap: '16px'
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<h3
							style={{
								margin: 0,
								fontSize: '18px',
								fontWeight: 600,
								color: '#0f172a',
								display: 'flex',
								alignItems: 'center',
								gap: '10px'
							}}
						>
							<div
								style={{
									width: '32px',
									height: '32px',
									borderRadius: '8px',
									backgroundColor: '#fee2e2',
									color: '#dc2626',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '14px',
									flexShrink: 0
								}}
							>
								<i className="fa-solid fa-clock-rotate-left" />
							</div>
							Revoke API Token
						</h3>
						<button
							onClick={() => setRevokeTargetToken(null)}
							style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
						>
							<i className="fa-solid fa-xmark" />
						</button>
					</div>

					<p style={{ margin: 0, fontSize: '13.5px', color: '#475569', lineHeight: '1.4' }}>
						Revoking <strong>{revokeTargetToken.name}</strong> will immediately or at a scheduled time reject all incoming request signatures using this token.
					</p>

					<form
						id="revoke-token-form"
						onSubmit={handleConfirmRevoke}
						style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={revokeImmediately}
									onChange={(e) => setRevokeImmediately(e.target.checked)}
									color="primary"
								/>
							}
							label={
								<span style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b' }}>
									Revoke immediately
								</span>
							}
						/>

						<div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
							<label style={{ fontSize: '13.5px', fontWeight: 500, color: '#475569' }}>
								Scheduled Revocation Date & Time
							</label>

							<DateTimePicker
								value={scheduledRevocationDate}
								onChange={(newValue) => setScheduledRevocationDate(newValue)}
								disabled={revokeImmediately}
								minDateTime={dayjs()}
								maxDateTime={revokeTargetToken.expiresAt ? dayjs(revokeTargetToken.expiresAt) : undefined}
								slotProps={{
									textField: {
										size: 'small',
										fullWidth: true,
										style: { backgroundColor: 'white', borderRadius: '8px' }
									}
								}}
							/>

							{!revokeImmediately && revokeTargetToken.expiresAt && (
								<span style={{ fontSize: '12px', color: '#64748b' }}>
									Must be before expiration date ({new Date(revokeTargetToken.expiresAt).toLocaleString()})
								</span>
							)}
						</div>

						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '10px',
								marginTop: '8px',
								paddingTop: '12px',
								borderTop: '1px solid #f1f5f9'
							}}
						>
							<Button
								variant="outlined"
								color="primary"
								type="button"
								onClick={() => setRevokeTargetToken(null)}
							>
								Cancel
							</Button>
							<Button
								color="error"
								type="submit"
								form="revoke-token-form"
								loading={isRevoking}
							>
								{revokeImmediately ? 'Revoke Now' : 'Schedule Revocation'}
							</Button>
						</div>
					</form>
				</SmallDialog>
			)}
		</section>
	);
};

export default ApiTokensSection;
